import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, RefreshCw, CheckCircle, LogOut } from 'lucide-react';
import { authApi } from '../api/auth';
import { useAuthStore } from '../store/authStore';

export default function VerifyEmailPage() {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const fetchCurrentUser = useAuthStore((state) => state.fetchCurrentUser);

  // Listen for verification from other tabs
  useEffect(() => {
    const channel = new BroadcastChannel('email-verification');
    channel.onmessage = async (event) => {
      if (event.data?.type === 'verified') {
        await fetchCurrentUser();
        navigate('/home');
      }
    };
    return () => channel.close();
  }, [fetchCurrentUser, navigate]);

  const handleResend = async () => {
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      await authApi.resendVerificationEmail(i18n.language);
      setSuccess(true);
    } catch (err: any) {
      if (err.response?.status === 429) {
        setError(t('verification.tooManyRequests'));
      } else {
        setError(err.response?.data?.message || t('verification.resendError'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-start sm:items-center justify-center bg-gradient-to-br from-dark-950 via-dark-900 to-dark-800 px-4 pt-16 sm:pt-0 pb-8">
      <div className="max-w-md w-full">
        <div className="text-center mb-6 animate-fade-in">
          <Link to="/" className="inline-flex items-center justify-center mb-3 hover:opacity-80 transition-opacity">
            <img src="/landing/logo-light.svg" alt="Fonorex" className="h-16" />
          </Link>
        </div>

        <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl shadow-2xl p-8 animate-scale-in">
          <div className="text-center">
            <div className="w-20 h-20 bg-gradient-to-r from-primary-600/20 to-secondary-600/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Mail className="w-10 h-10 text-primary-400" />
            </div>

            <h1 className="text-2xl font-bold text-white mb-2">
              {t('verification.title')}
            </h1>

            <p className="text-gray-400 mb-6">
              {t('verification.description')}
            </p>

            {user?.email && (
              <p className="text-gray-300 bg-white/5 rounded-lg px-4 py-2 mb-6">
                {user.email}
              </p>
            )}

            {error && (
              <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 text-red-200 rounded-xl backdrop-blur-sm">
                {error}
              </div>
            )}

            {success && (
              <div className="mb-6 p-4 bg-green-500/20 border border-green-500/50 text-green-200 rounded-xl backdrop-blur-sm flex items-center justify-center gap-2">
                <CheckCircle className="w-5 h-5" />
                {t('verification.resendSuccess')}
              </div>
            )}

            <button
              onClick={handleResend}
              disabled={loading || success}
              className="w-full bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-500 hover:to-secondary-500 text-white font-bold py-4 px-4 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-glow flex items-center justify-center gap-2"
            >
              {loading ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                <RefreshCw className="w-5 h-5" />
              )}
              {loading ? t('verification.resending') : t('verification.resendButton')}
            </button>

            <p className="mt-6 text-sm text-gray-500">
              {t('verification.checkSpam')}
            </p>

            <button
              onClick={logout}
              className="mt-6 text-gray-400 hover:text-white transition-colors flex items-center justify-center gap-2 mx-auto"
            >
              <LogOut className="w-4 h-4" />
              {t('verification.logout')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
